import { Center, Flex, Text } from '@chakra-ui/react';
import { decode } from 'js-base64';
import dayjs from 'dayjs';
import { Feather } from '@kula-ai/icons-core';
import { KButton } from '@/ui-library/components';
import { useMarkAsReadMutation } from '@/store/query/notification';
import { NotificationCard } from '../../components/Card';
import { NotificationTime } from '../../components/Time';
import { NotificationLink } from '../../components/NotificationLink';
import {
  RequisitionDetailsSection,
  RequisitionDetailsList,
} from '../requisition-notifications/components';

interface ConsentData {
  status?: string;
  response?: string;
  requested_on?: string;
}

interface CandidateData {
  id?: number;
  name?: string;
  avatar_url?: string | null;
  url?: string;
}

interface Notification {
  id?: string | number;
  custom_attributes?: string;
  [key: string]: any;
}

interface Props {
  notification?: Notification;
  onClose?: () => void;
}

// Mirrors the backend response_verb (Notifications::Ats::CandidateConsent) — the
// notification only fires on grant/refuse, so those are the meaningful branches.
const verbFor = (status?: string) => {
  if (status === 'granted' || status === 'no_expiry') return 'accepted';
  if (status === 'refused') return 'refused';
  return 'responded to';
};

export const CandidateConsentRespondedNotification = ({
  notification = {},
  onClose = () => {},
}: Props) => {
  const [markAsRead] = useMarkAsReadMutation();
  let data: { candidate?: CandidateData; consent?: ConsentData } = {};
  try {
    data = JSON.parse(decode(notification.custom_attributes || ''));
  } catch (error) {
    console.error('Error decoding consent responded notification:', error);
  }

  const { candidate = {}, consent = {} } = data;
  const verb = verbFor(consent.status);

  const detailsData = [
    { label: 'Response', value: consent.response },
    {
      label: 'Requested on',
      value: consent.requested_on
        ? dayjs(consent.requested_on).format('DD/MM/YYYY')
        : undefined,
    },
  ].filter((item) => item.value);

  const viewCandidate = () => {
    markAsRead({ id: notification?.id });
    window.open(`/candidates/${candidate.id}`, '_self');
    onClose();
  };

  return (
    <NotificationCard notification={notification}>
      <Flex alignItems="baseline" gap="6px">
        <Center minW="34px" h="34px" borderRadius="sm" bg="primary-100">
          <Feather
            size="1em"
            color="var(--kula-colors-primary-500)"
            className="kula-icon"
          />
        </Center>
        <Flex flexDir="column" ml="12px" gap="8px" alignSelf="start" flex="1">
          <Text fontSize="sm">
            <NotificationLink
              to={`/candidates/${candidate.id}`}
              label={candidate.name}
              onClose={onClose}
              notification={notification}
              data-test-id="candidate-link"
            />
            has {verb} your consent request
          </Text>

          <RequisitionDetailsSection title="Consent details">
            <RequisitionDetailsList data={detailsData} />
          </RequisitionDetailsSection>

          <NotificationTime notification={notification} />
          {candidate.id && (
            <KButton
              w="fit-content"
              variant="primary"
              borderRadius="8px"
              maxH="34px"
              onClick={viewCandidate}
              data-test-id="view-candidate-button"
            >
              View candidate
            </KButton>
          )}
        </Flex>
      </Flex>
    </NotificationCard>
  );
};
